/**
 * ⛔⛔ THE FOUR-STEP REGENERATE CHAIN, EXTRACTED SO IT CAN BE TESTED (task-aodbhweog0).
 *
 * It lived inside a ~4,000-line page component, where the only way to exercise it was to render the
 * whole page — so both codex reviewers noted, twice, that request ordering, `dryRun:false`, stopping
 * after each failed phase, malformed 200s and verification failure were all changed behaviour with
 * nothing testing them. One of them named the remedy: extract it. This is that.
 *
 * ⛔ WHY THE CHAIN EXISTS AT ALL: a plain `refresh/personas` on a weighted category is REFUSED.
 * `preserveBenefitWeights` matches stored→researched personas by `persona_key`; an archetype recreate
 * invents new keys, so every weighted persona strands and the write throws.
 *
 * ⭐⭐ SUPERSEDED 2026-09-07, AND THE OLD RATIONALE IS KEPT ABOVE ON PURPOSE — a reader who saw it
 * needs to know it was replaced, not find it vanished. It used to continue: "The clear removes the
 * strandable state deliberately, which is what makes the refresh legal rather than a bypass."
 * ⇒ THAT WAS TRUE AND IT WAS THE PROBLEM. The clear's only purpose was to disarm the guard, so the
 *   chain performed a destructive write in order to switch off the protection for the data it was
 *   destroying — and every later failure landed after the loss. The refresh is now permitted by an
 *   AUDITED, OPT-IN overwrite instead, so the destructive write IS the research's own write and
 *   happens only if the research succeeded. Three steps, not four.
 * ⚠️ The clear endpoint still exists and still works; nothing in production calls it. Its fate is
 *   task-dttz3mfkd8, deliberately not decided here: retiring a live capability inside the change
 *   that orphans it would couple two independently reversible things.
 *
 * 🔑 EVERY STEP VALIDATES THE DOCUMENTED ENVELOPE, NOT JUST `response.ok`. A 200 carrying
 * `{success:false}` or a junk body used to advance the chain — and worse, could switch OFF the
 * ambiguity guard, because "was the clear a no-op" was read straight off the body. An unrecognised
 * reply now fails CLOSED: treated as possibly-committed, never as a harmless no-op.
 */

/**
 * ⛔ 'checking' IS FIRST BECAUSE IT RUNS FIRST — the readiness preflight, before anything
 * destructive. Adding it here is a breaking change for any consumer that DUPLICATED this union
 * inline instead of importing it, and CategoryResearch.tsx did exactly that in two places. Those
 * are now the imported type, so the next member added here reaches the screen instead of silently
 * type-erroring at the setter.
 */
export type RegenPhase = 'checking' | 'recreating' | 'weighting' | 'verifying';

export interface RegenerateChainDeps {
  /** Authenticated fetch. Injected so tests drive the chain without a network or a rendered page. */
  authFetch: (url: string, init?: RequestInit) => Promise<Response>;
  /** Base URL for this category, without a trailing slash. */
  base: string;
  /** Reports the step about to run, so the caller can show it. */
  onPhase: (phase: RegenPhase) => void;
  /**
   * ⛔⛔ THE STAMP THE OPERATOR WAS LOOKING AT when they pressed the button — NOT one this chain
   * reads for itself a millisecond earlier. Reading it here would make the precondition compare a
   * value against one taken moments before, which always matches: protection-shaped and empty, the
   * same defect as the self-comparing check removed earlier. The whole point is "has this row moved
   * since the screen was drawn", and only the caller knows what the screen showed.
   * ⚠️ Optional: when the card has no stamp the clear runs unguarded, exactly as before. That is a
   * degraded mode, not a silent one — it is stated here and the chain still has its later checks.
   */
  expectedUpdatedAt?: string | null;
  /** The row's research status, as the screen last saw it. See the preflight below. */
  status?: string | null;
  /**
   * ⛔⛔ REQUIRED, NOT OPTIONAL, AND THAT IS THE WHOLE POINT (codex, BOTH LEGS BLOCKING @8c4fb6c2f).
   *
   * The readiness preflight can return `ready: true` WITH a `notChecked` warning: for a discovered
   * sub-category the product sample is drawn at random, so no check can predict what the refresh
   * will see. The server deliberately permits those — refusing them would block roughly half the
   * estate — and says so in the reply.
   * ⇒ The first version of this then read ONLY `ready` and dispatched the clear. Both reviewers
   *   reproduced it. The server carried the uncertainty honestly and the client threw it away, so
   *   the operator destroyed curated weights without ever being told the check had not been made.
   * 🔑 A FACT THE CODE KNOWS MUST BE CARRIED AS A VALUE — this file says exactly that, about
   *   `weightsWritten`, eighty lines down. I carried it as a value across the wire and dropped it
   *   at the consumer, which is the same defect with a longer journey.
   *
   * ⚠️ It is REQUIRED so that no caller can reintroduce that by omission. An optional callback
   * would default to "proceed", and a default that silently proceeds past an unverified
   * precondition is the silent fallback this repo forbids — added at the one seam where the next
   * step is irreversible. Making it mandatory means a new caller has to DECIDE, and the type
   * checker asks the question.
   *
   * ⭐⭐ CONTRACT CHANGED (task-3b3e5t9b9g): this receives THE FINISHED MESSAGE, not the raw
   * warnings. It used to take `string[]` and the PAGE composed the prose around them — which put
   * the single most consequential sentence in this feature (the one an operator reads immediately
   * before authorising a destructive, paid write) in the half of the surface that no test executes.
   * ⇒ FOUR OF THE ELEVEN-ODD FALSE-CLAIM SIBLINGS IN #5789 SURVIVED IN THAT HALF, AND THEY
   *   SURVIVED BECAUSE IT IS THE HALF NOTHING EXERCISES. The chain was extracted so it could be
   *   tested; the copy the operator actually READS was left behind.
   * ⚠️ The caller's job is now to SHOW the message and answer, not to author claims about what the
   *   server did. A caller cannot reintroduce a sibling here without changing this file.
   *
   * @param message the complete, testable dialog text — show it verbatim
   * @returns true to proceed under the stated uncertainty, false to stop with nothing changed.
   */
  confirmUnverified: (message: string) => boolean | Promise<boolean>;
}

export interface RegenerateChainResult {
  personaCount: number;
  weightedCount: number;
  /** Empty when nothing was discarded. Appended to whatever the caller reports. */
  discardedNote: string;
  /** The canonical-layer sentence from the weighting step (see describeCanonicalization). Never empty on success. */
  canonicalizationNote: string;
}

export class RegenerateChainError extends Error {
  /**
   * ⛔⛔ TRUE ONLY WHILE THE OUTCOME IS GENUINELY UNKNOWN — not merely "something was sent".
   *
   * The destructive request (the RECREATE; there is no clear any more) was dispatched, and nothing
   * since has established whether it committed. That is the case where the operator has least
   * information and the caller must say so.
   * ⚠️ IT GOES BACK TO FALSE as soon as the recreate answers 2xx WITH `success: true` — which the
   *   route sends only after its COMMIT — because at that point we know what happened. It used to
   *   stay true forever, so a category whose two writes had BOTH SUCCEEDED and whose verification
   *   read merely 503'd was reported as possibly-uncommitted, and the operator was told to re-run:
   *   a paid step repeated for nothing, and its record overwriting the only copy of the vectors the
   *   first run discarded.
   * ⚠️ NOT gated on the audit acknowledgement, deliberately — see the assignment site. That gate
   *   answers a different question, and putting this after it made one toast say both "your curated
   *   benefit weights are intact" and "they may already have been replaced".
   */
  readonly weightsMayBeGone: boolean;
  readonly discardedNote: string;
  /** Empty until the weighting step has answered; then the same sentence a success would carry. */
  readonly canonicalizationNote: string;
  constructor(message: string, weightsMayBeGone: boolean, discardedNote: string, canonicalizationNote = '') {
    super(message);
    this.name = 'RegenerateChainError';
    this.weightsMayBeGone = weightsMayBeGone;
    this.discardedNote = discardedNote;
    this.canonicalizationNote = canonicalizationNote;
  }
}

/**
 * ⛔⛔ THE ADVICE FOR THE ONE CASE THAT IS GENUINELY UNKNOWN — AND IT USED TO DESTROY THE RECORD IT
 * EXISTS TO PROTECT (codex leg 2, minor at 6ab2156ff).
 *
 * It ended: "if they are [the new personas], re-run this button to finish the regeneration."
 * ⇒ But "they are the new personas" MEANS THE RECREATE COMMITTED. A full re-run therefore performs
 *   a SECOND recreate, which writes a new `research_metadata.benefit_weight_replaced` OVER the
 *   first — and the first is the only copy of the CURATED vectors. The second record would describe
 *   the machine-generated vectors that run one produced. So the advice, followed exactly, converts
 *   a recoverable loss into an unrecoverable one.
 * 🔑 THE RECOVERY ADVICE DESTROYS THE RECOVERY RECORD. That is the second time in this PR that a
 *   safety message's failure mode was deleting the evidence the PR was built to preserve, and both
 *   times it read as caution. A warning that recommends the destructive action is worse than no
 *   warning, because the operator follows it BELIEVING they are being careful.
 * ⚠️ The remaining work after a committed recreate is only the weighting, and "Repair benefit
 *   weights" does exactly that without touching the record.
 *
 * ⛔⛔ AND THE FIX ITSELF SHIPPED A FALSE CERTAINTY, CAUGHT ONE COMMIT LATER (codex leg 1, minor at
 * 907ff5670). It ended "IF THEY ARE NOT: nothing committed, and re-running this button is safe."
 * Both halves are wrong, and I verified both rather than taking them:
 *   - THE REQUEST MAY STILL BE RUNNING. The route awaits the provider at
 *     category-characteristics.js:2005 and does not open its transaction until :2197 — almost two
 *     hundred lines later. A client-side transport failure does not stop the Express handler, so it
 *     can finish researching and COMMIT AFTER THE OPERATOR HAS LOOKED. "The personas are still the
 *     old ones" is a fact about the moment of looking, not about the outcome.
 *   - "UNCHANGED" IS NOT OBSERVABLE ANYWAY in the all-matched case, where every persona_key is
 *     carried across, so the same keys remain in place whether or not the write landed.
 * 🔑 I REMOVED FOUR FALSE CERTAINTIES TODAY AND THEN WROTE A FIFTH INTO THE SENTENCE THAT FIXED
 *   ONE OF THEM. A correction is a new claim and inherits none of the original's discipline — and
 *   the word "safe" is the one that should have triggered the check, because it is a guarantee.
 * ⇒ The advice now points at facts the operator can actually READ — the revision timestamp and the
 *   presence of the record — instead of at an appearance they cannot reliably judge.
 *
 * ⛔ EXPORTED FROM HERE RATHER THAN WRITTEN IN THE PAGE, DELIBERATELY AND NARROWLY. This copy lived
 * in CategoryResearch.tsx, the half of the operator surface that nothing executes — which is where
 * four of this PR's five false-claim siblings survived (task-3b3e5t9b9g). Moving ONE string to the
 * tested module makes it coverable now; it is not the full topology fix, which changes the
 * `confirmUnverified` contract and is deliberately not being done at the tail of this review cycle.
 */
export const AMBIGUOUS_RECREATE_ADVICE =
  ` ⚠️ The recreate had already been sent when this failed, so it MAY ALREADY HAVE COMMITTED —`
  + ` replacing the curated benefit weights — even though the error looks like a transport problem.`
  + ` Re-open the category and check whether the personas are the new ones before doing anything`
  + ` else. IF THEY ARE: the recreate committed, so press "Repair benefit weights" to finish. Do NOT`
  + ` re-run this button — a full re-run recreates again and writes a new replacement record over`
  + ` research_metadata.benefit_weight_replaced, which is the ONLY copy of the curated vectors the`
  + ` first run replaced; copy that record first if you may need it. IF THEY LOOK UNCHANGED, THAT`
  + ` DOES NOT MEAN NOTHING COMMITTED: the request may still be running on the server and commit`
  + ` after you look, and a regeneration in which every persona key matched leaves the same keys in`
  + ` place, so it can look unchanged either way. Judge it by the category's revision timestamp and`
  + ` by whether research_metadata.benefit_weight_replaced is present and new — not by whether the`
  + ` personas look familiar — and copy that record before any re-run.`;

/**
 * ⛔⛔ HOW TO FINISH AFTER THE RECREATE HAS COMMITTED — AND WHY "RE-RUN" IS NOT AN EQUAL OPTION.
 *
 * Found by running the census orchestrator's trigger implies, NOT by a reviewer: enumerate every
 * path that WRITES `benefit_weight_replaced`, then check every operator instruction that leads to
 * one. There is exactly ONE write site (recordBenefitReplacement.js:118) and FOUR instructions that
 * offered "or re-run this button" in states where the recreate had ALREADY COMMITTED — the two
 * weighting-failure branches, the weighting transport failure, and the weighting junk-200.
 * ⇒ In every one of those the record EXISTS, and a full re-run performs a second recreate that
 *   writes a NEW record over it. The first holds the CURATED vectors; the second would hold the
 *   machine-generated ones the first run produced. Offering the two actions as equals invited the
 *   destructive one.
 * 🔑 THE INSTRUCTIONS WERE ALL WRITTEN BEFORE THE RECORD EXISTED, and adding a durable artefact
 *   silently invalidated them. Reviewing the artefact could never surface it: both legs listed the
 *   audit record under NOTABLE WINS in the same review where one of them found this. It was
 *   reviewed as a WIN, not as a thing with a LIFETIME, so nobody asked what erases it.
 * ⚠️ ONE CONSTANT, used by all four, because four divergent copies of this sentence is precisely
 *   the condition that produced the siblings (task-3b3e5t9b9g).
 */
const FINISH_WITHOUT_ERASING_THE_RECORD =
  ` Press "Repair benefit weights" to finish — it completes the weighting and does NOT touch the`
  + ` audit record. Do not re-run this button instead: a full re-run recreates the personas again`
  + ` and writes a new replacement record over research_metadata.benefit_weight_replaced, which is`
  + ` the only copy of the curated vectors this run replaced. Copy it first if you may need it.`;

/**
 * ⛔⛔ THE THREE OPERATOR-FACING COMPOSITIONS, MOVED OUT OF THE PAGE (task-3b3e5t9b9g).
 *
 * Every one of these was authored in CategoryResearch.tsx — a ~4,000-line component with no direct
 * test of the composed string — while the chain beside it had 73 unit tests. That split is not a
 * tidiness problem, it is the CONDITION that produced sibling after sibling of one false claim in
 * #5789: each fix landed in the tested half, and the next round found the same sentence in the
 * untested one.
 * 🔑 THE EVIDENCE IS AN INDEPENDENT REVIEWER READING UNCHANGED BYTES TWICE. codex leg 2 reviewed
 *   the identical text at two shas and raised it only the second time, after the louder defect it
 *   had itself found was fixed — so a structural condition yields exactly ONE review round per
 *   sibling, forever. Review peels a layer per round; it cannot exit the condition.
 * ⇒ So the page now RENDERS what it is given and authors no claims about what the server did.
 *
 * ⚠️ THIS IS DELIBERATELY NOT A LINTER OR A FORBIDDEN-VOCABULARY SCANNER. That was declined twice
 *   during #5789 and nothing has changed: the invariant ("a message must not assert a state the
 *   code cannot establish") is SEMANTIC, so a grep can only encode the sentences already found
 *   wrong — the enumeration trap wearing a policy's costume, and worse than nothing because it
 *   would read as protection. Moving the text somewhere tests can reach it is the structural fix;
 *   policing its wording is not.
 */

/** The dialog an operator reads immediately before authorising a destructive, paid write. */
export function describeUnverifiedConfirmation(warnings: string[]): string {
  return 'If the research succeeds it will REPLACE the curated benefit weights (recorded on the '
    + 'category) and the weighting step that follows will create new ones. If the RESEARCH '
    + 'fails, nothing is changed. If the weighting fails AFTER it, the replacement has '
    + 'already committed and it is NOT known whether the purpose-researched weights landed — '
    + 'that step commits as soon as its update succeeds, so an error can be reported after '
    + 'the write. The category is usable either way; re-open it to see which weights it '
    + 'carries before repeating a paid research step. '
    + 'Part of the readiness check could not be verified:\n\n'
    + warnings.join('\n\n')
    + '\n\nProceed anyway?';
}

/** What landed, for the success toast. */
/**
 * The canonical-layer sentence appended to the operator's note after the weighting step. Reads the
 * server's `canonicalization` summary and the two reason lists; never derives a count from the
 * nulls in canonicalPersonaIds, because a null carries no reason and this note exists to give one.
 */
export function describeCanonicalization(result: any): string {
  const summary = result?.canonicalization;
  if (!summary || typeof summary !== 'object') {
    return ' ⚠️ This server did not report the canonical-persona summary, so it is NOT known whether'
      + ' the recreated personas were matched to canonical personas — an older backend, not a clean'
      + ' result.';
  }
  const attempted = Number(summary.attempted);
  const mapped = Number(summary.mapped);
  const unmappedCount = Number(summary.unmapped);
  const failedCount = Number(summary.failed);
  if (![attempted, mapped, unmappedCount, failedCount].every((n) => Number.isInteger(n) && n >= 0)) {
    return ' ⚠️ This server reported a canonical-persona summary this page could not read, so it is'
      + ' NOT known whether the recreated personas were matched to canonical personas.';
  }
  if (attempted === mapped && failedCount === 0) {
    return ` All ${mapped} persona(s) are matched to a canonical persona.`;
  }
  const names = (list: unknown): string => (Array.isArray(list)
    ? list.map((e: any) => (typeof e?.persona === 'string' && e.persona !== '' ? e.persona : '(unnamed)')).join(', ')
    : '(not listed)');
  const parts: string[] = [];
  if (unmappedCount > 0) {
    // ⛔ THE REASON IS THE SERVER'S, RENDERED PER GROUP — codex #5835 round 1: this line hard-coded
    // "no synonym-map entry" for every unmapped persona, so a persona the server had marked
    // `no_name` (nothing to look up, no lookup made) was given the wrong diagnosis and the wrong
    // remedy. Group by the supplied reason and say each in its own words; an unknown reason is
    // shown as given rather than translated into one of the known ones.
    parts.push(`${unmappedCount} of ${attempted} persona(s) have NO canonical persona: `
      + describeUnmappedByReason(result?.canonicalization_unmapped));
  }
  if (failedCount > 0) {
    parts.push(`${failedCount} canonical lookup(s) FAILED: ${names(result?.canonicalization_failures)}`);
  }
  return ` ⚠️ ${parts.join('; ')}. Their canonical_persona_id is null, so nothing keyed on the canonical`
    + ' layer will find them until they are mapped.';
}

/** The operator's words for each unmapped reason the server can send; anything else is shown verbatim. */
export const UNMAPPED_REASON_TEXT: Record<string, string> = {
  no_synonym_map_entry: 'no synonym-map entry for their name',
  no_name: 'no name to look up, so no lookup was made',
};

export function describeUnmappedByReason(list: unknown): string {
  if (!Array.isArray(list)) return '(not listed)';
  const groups = new Map<string, string[]>();
  for (const e of list as any[]) {
    const reason = typeof e?.reason === 'string' && e.reason !== '' ? e.reason : 'reason not given';
    const name = typeof e?.persona === 'string' && e.persona !== '' ? e.persona : '(unnamed)';
    groups.set(reason, [...(groups.get(reason) ?? []), name]);
  }
  return [...groups.entries()]
    .map(([reason, names]) => `${UNMAPPED_REASON_TEXT[reason] ?? reason}: ${names.join(', ')}`)
    .join('; ');
}

export function describeChainSuccess(result: RegenerateChainResult): string {
  return `${result.personaCount} persona(s), ${result.weightedCount} carrying benefit weights — `
    + 'this category will score against its own personas rather than the sector fallback.'
    + result.discardedNote
    + (typeof result.canonicalizationNote === 'string' ? result.canonicalizationNote : '');
}

/**
 * The failure toast, composed: the step's own message, what was discarded, and — ONLY when the
 * outcome is genuinely unknown — the ambiguity advice.
 * ⛔ ACCEPTS `unknown`, NOT `RegenerateChainError`. A TypeError escaping the chain is not one, and
 * the page's old inline version read `err?.discardedNote` off whatever it caught; that tolerance
 * has to survive the move or a non-chain throw renders as "undefined".
 * ⚠️ The ambiguity suffix is appended on `weightsMayBeGone` ALONE. That flag is false once the
 * recreate's reply is validated, so a run whose writes both succeeded and whose verification
 * merely failed does NOT get told to re-run — advice which would overwrite the only copy of the
 * replaced vectors.
 */
export function describeChainFailure(err: unknown): string {
  const e = err as { message?: unknown; discardedNote?: unknown; weightsMayBeGone?: unknown; canonicalizationNote?: unknown } | null;
  const message = typeof e?.message === 'string' ? e.message : String(err);
  const discarded = typeof e?.discardedNote === 'string' ? e.discardedNote : '';
  const canonical = typeof e?.canonicalizationNote === 'string' ? e.canonicalizationNote : '';
  const ambiguous = e?.weightsMayBeGone === true ? AMBIGUOUS_RECREATE_ADVICE : '';
  return `${message}${discarded}${canonical}${ambiguous}`;
}

/** Reads the server's own error text rather than inventing one — the backend's refusals are written
 *  for an operator (the strand refusal names every persona it would have lost). */
async function failureOf(res: Response, fallback: string): Promise<string> {
  try {
    const body = await res.json();
    return body?.error || fallback;
  } catch { return fallback; }
}

/** The same, from an ALREADY-PARSED body. A Response body can only be read once, and the clear's is
 *  now read a single time up front so the same object serves the refusal message, the
 *  `weightsWritten` fact and the success validation. */
function failureFrom(body: any, fallback: string): string {
  return body?.error || fallback;
}

export async function runRegeneratePersonasChain(
  deps: RegenerateChainDeps,
): Promise<RegenerateChainResult> {
  const { authFetch, base, onPhase, expectedUpdatedAt = null, status = null, confirmUnverified } = deps;

  /**
   * ⛔⛔ SET BEFORE THE REQUEST IS SENT, NOT AFTER IT RESOLVES. Once the clear has been DISPATCHED we
   * can no longer know whether it committed: a dropped connection or a truncated body fails on the
   * client while the server has already written. An earlier version only warned on paths where the
   * response PARSED — i.e. everywhere except the case that most needs the warning.
   * 🔑 A "no silent partial" guarantee that depends on the reply arriving is a guarantee about
   *   REPLIES, not about partials.
   */
  /**
   * ⛔⛔ SET BEFORE THE REQUEST IS SENT, NOT AFTER IT RESOLVES. Once the recreate has been
   * DISPATCHED we can no longer know whether it committed: a dropped connection or a truncated
   * body fails on the client while the server has already written.
   * 🔑 A "no silent partial" guarantee that depends on the reply arriving is a guarantee about
   *   REPLIES, not about partials.
   *
   * ⭐ THIS IS THE SAME PROPERTY THE OLD `clearDispatched` CARRIED, ABOUT A DIFFERENT STEP — the
   * ambiguity window moved when the destruction moved. It is RE-POINTED, not removed, and that
   * distinction matters to anyone reading the diff: a deleted assertion and a relocated one look
   * identical unless someone says which happened.
   * ⚠️ AND IT NEEDS NO `clearWasNoop`/`refusedBeforeWrite` COMPANIONS ANY MORE. Those existed
   * because the CLEAR could report, definitively, that it had written nothing — a 4xx from a
   * separate destructive step. The recreate has no such state: either it committed or we do not
   * know. Removing them is the shape of the chain changing, not a guarantee being dropped.
   */
  let recreateDispatched = false;
  /**
   * ⛔⛔ SET WHEN THE SERVER REFUSED *BEFORE* WRITING ANYTHING — and I removed this in the first
   * draft of the reorder, calling it "genuinely gone with the clear". IT IS NOT. The old suite's
   * `weightsWritten` tests caught it: without this, a 422 ineligibility or a 409 moved-row from the
   * recreate would report `weightsMayBeGone: true`, because `recreateDispatched` flips as soon as
   * the request leaves. That is a FALSE ALARM about lost curated data on a path where the server
   * explicitly rolled back.
   * 🔑 The property was never about the CLEAR — it was about a destructive step that can refuse
   *   definitively. It moved with the destruction, exactly as the precondition and the eligibility
   *   gate did. Deleting it would have looked identical in a diff to relocating it.
   * ⚠️ ABSENT IS UNKNOWN, NOT FALSE. A 500, an error-middleware reply or a dropped connection
   *   carries no field, and every one of those can be a commit that failed with the UPDATE already
   *   executed. Only an explicit `false` makes this definitive.
   */
  let refusedBeforeWrite = false;
  /**
   * ⛔⛔ THE RECREATE'S OUTCOME IS NO LONGER UNKNOWN (codex leg 2, minor at d7ba96287).
   *
   * `weightsMayBeGone` was `recreateDispatched && !refusedBeforeWrite`, which models "the recreate
   * was SENT" — and never becomes false again. So when BOTH writes succeed and only the VERIFY read
   * fails, the component appended its generic ambiguity warning to a message that correctly said
   * both steps had landed: "it MAY ALREADY HAVE COMMITTED … re-run this button to finish".
   * ⛔ AND THAT ADVICE IS ACTIVELY HARMFUL HERE, WHICH IS WHY IT IS NOT MERELY NOISE. Re-running
   *   repeats a PAID research step that already succeeded, and its replacement record OVERWRITES
   *   `benefit_weight_replaced` — the ONLY copy of the discarded vectors. The generic warning would
   *   destroy the very recovery record the rest of this PR exists to create.
   * 🔑 A FLAG NAMED FOR A CAUSE ("was it sent") CANNOT ANSWER A QUESTION ABOUT A STATE ("do we
   *   know what happened"). It was right at the moment it was introduced — dispatch WAS the last
   *   thing we knew — and it silently stopped being right once a later step could confirm the
   *   outcome. Nothing about the flag changed; what changed is that a fact arrived after it.
   * ⚠️ DELIBERATELY NOT A RENAME of the public field. `weightsMayBeGone` stays accurate under the
   *   corrected rule — after a confirmed recreate there is no "may" — and renaming an exported
   *   readonly field at the end of a review cycle is churn that invites the miss it is meant to
   *   prevent. The rule is stated here instead.
   */
  let recreateConfirmed = false;
  let discardedNote = '';
  let canonicalizationNote = '';

  const fail = (message: string): never => {
    throw new RegenerateChainError(
      message,
      recreateDispatched && !refusedBeforeWrite && !recreateConfirmed,
      discardedNote,
      canonicalizationNote,
    );
  };

  /**
   * ⛔⛔ EVERY REQUEST GOES THROUGH HERE, AND THE REASON IS THE SAME DEFECT TWICE OVER.
   * A REJECTED promise — connection lost, DNS, abort — used to escape as a raw Error, so the
   * component's catch found no `weightsMayBeGone` and no `discardedNote` and printed the bare
   * network message. ⇒ THE ONE FAILURE MODE THE AMBIGUITY WARNING EXISTS FOR (the clear was sent,
   * we never learned whether it committed) WAS THE ONE THAT SILENTLY SKIPPED IT.
   * 🔑 Identical in shape to the junk-200 case fixed a commit earlier: a guard that switches itself
   *   off on the exact input it was written for. That one was the BODY path; this is the PROMISE
   *   path. Fixing one and not the other is how a class survives its own remedy.
   */
  const request = async (url: string, init: RequestInit | undefined, whenLost: string): Promise<Response> => {
    try {
      return await authFetch(url, init);
    } catch (e: any) {
      return fail(`${whenLost} (${e?.message || 'connection failed'})`);
    }
  };

  // ── 1. CLEAR ──────────────────────────────────────────────────────────────────────────────────
  /**
   * ⛔⛔ ELIGIBILITY PREFLIGHT — REFUSE BEFORE THE DESTRUCTIVE STEP, NOT AFTER IT.
   *
   * The weighting service that finishes this chain only runs on an ACTIVE row. Without this, a
   * non-active category went: clear the curated weights, replace the personas, THEN fail — losing
   * the vectors and leaving generation on the sector fallback. Discovering ineligibility after the
   * irreversible step is the same defect as having no check.
   * ⚠️ THIS IS NOT THE GUARD, and it must not be mistaken for one. It runs on what the SCREEN last
   *   saw, so it is only as fresh as the card; the authoritative refusal is the 422 from the clear
   *   endpoint, which is evaluated inside the transaction before the UPDATE and cannot be skipped
   *   by a caller that never loaded this page. This check exists to give the operator an immediate
   *   reason and to avoid dispatching a request that is certain to be refused.
   * ⚠️ Skipped when the caller passes no status: refusing on an absent value would disable the
   *   button for every caller that has not been updated to supply it, which is a different way of
   *   breaking the feature. The server still refuses.
   */
  if (status != null && status !== 'active') {
    fail(
      `This category is "${status}", not "active", so nothing was changed. A persona regeneration `
      + `finishes by re-weighting the personas, and that step only runs on active categories — `
      + `starting here would discard the curated benefit weights and then fail, leaving the `
      + `category with none at all. Re-research this category first.`,
    );
  }

  /**
   * ── 0. READINESS PREFLIGHT ────────────────────────────────────────────────────────────────────
   * ⛔⛔ THE ONE CHECK ABOVE WAS NEVER THE SET. The eligibility guard covers `status`, which is a
   * precondition of steps 1 and 3. It says nothing about STEP 2, and step 2 is where this chain
   * actually fails: it is the step that ran, after the clear, on the category Jaume brought us.
   *
   * ⛔ AND THE FIX IS NOT A SECOND HARDCODED CHECK. Adding "…and canonical benefits exist" beside
   * the status check would fix the instance we were shown and leave the class untouched — which is
   * the defect this repo has paid for repeatedly, most recently one PR ago. So the server derives
   * the SET by reading step 2's handler top to bottom (fourteen exits, three of them preflightable)
   * and this asks for that verdict. The derivation lives in ONE place,
   * backend-merged/services/persona/personaRegenerationReadiness.js, and is not restated here.
   *
   * ⚠️ ADVISORY, EXACTLY LIKE THE STATUS CHECK ABOVE. It runs before the request and can be stale
   * by the time step 2 executes; the handler's own checks remain the guarantee. It exists to refuse
   * before the irreversible step and to give the operator a reason.
   *
   * ⛔⛔ A PREFLIGHT THAT CANNOT BE READ MUST REFUSE, NOT WAVE THROUGH. A non-2xx, a junk body, or a
   * missing `ready` field all mean WE DID NOT VERIFY — and this chain's next action is irreversible.
   * Treating an unreadable preflight as permission would rebuild, at the safest-looking seam
   * available, the exact junk-200 defect the rest of this file was rewritten to remove.
   * ⚠️ `ready !== true`, never `!ready` — an absent field must not be coerced into a decision.
   */
  onPhase('checking');
  const readyRes = await request(`${base}/persona-regeneration-readiness`, undefined,
    'The connection failed while checking whether this category can be regenerated; nothing was changed');
  if (!readyRes.ok) {
    fail(
      `Could not check whether this category is ready to regenerate (${await failureOf(readyRes, `HTTP ${readyRes.status}`)}). `
      + 'Nothing was changed. The first step of this operation permanently discards the curated '
      + 'benefit weights, so it will not run on an unverified precondition — try again.',
    );
  }
  let readiness: any;
  try {
    readiness = await readyRes.json();
  } catch {
    readiness = null;
  }
  /**
   * ⛔ HOISTED OUT OF THE REFUSAL BRANCH: it now serves BOTH the `ready !== true` diagnostics and
   * the `notChecked` warning that arrives WITH `ready: true`. Declaring it inside the refusal
   * branch and reaching for it from the success path is a scope error the compiler catches —
   * but the reason it must be ONE helper is not scope, it is that both fields carry the same
   * contract and a second copy would drift on exactly the malformed shapes it exists to catch.
   */
  const messagesOf = (v: any, decorate?: (m: string) => string): string[] | null => {
    if (v === undefined || v === null) return [];
    if (!Array.isArray(v)) return null;
    /**
     * ⛔⛔ A MALFORMED ENTRY MAKES THE WHOLE LIST UNREADABLE. It used to be FILTERED OUT, and
     * codex — both legs, minor at f57c1cf8e — reproduced why that is not survivable here:
     * `{ready:true, notChecked:[{}]}` filtered down to `[]`, which reads as "no warnings", and the
     * chain dispatched the destructive clear with no confirmation at all. Also reproduced with
     * `[null]` and with an entry whose message is the empty string.
     *
     * 🔑 ONE HELPER, TWO CALL SITES, AND THE SAME BEHAVIOUR IS SAFE AT ONE AND UNSAFE AT THE OTHER.
     * On the REFUSAL path, dropping a bad entry only loses a sentence from a path that is failing
     * anyway — it fails closed. On the WARNING path it converts "there is a warning" into "there is
     * none" — it fails OPEN, at the one seam where the next step is irreversible. I hoisted this
     * helper so both fields would share one contract; sharing made them CONSISTENT, and consistency
     * is not correctness. The shared contract itself had to be the strict one.
     *
     * ⇒ And it makes the mixed-shape rule apply one level down, where it was already stated one
     *   level up: a list where some entries are readable and some are not is not half a diagnosis
     *   to be reported, it is a reply we did not understand. Half a diagnosis reads exactly like a
     *   whole one.
     */
    const out: string[] = [];
    for (const e of v) {
      if (!e || typeof e.message !== 'string' || e.message === '') return null;
      out.push(decorate ? decorate(e.message) : e.message);
    }
    return out;
  };

  if (!readiness || readiness.ready !== true) {
    /**
     * ⛔ THE SERVER'S OWN MESSAGES, JOINED — NOT A SUMMARY WRITTEN HERE. The canonical-benefits
     * refusal in particular already measures WHICH of four states produced it and what to do about
     * each; paraphrasing it on the client would give an operator a second, worse diagnosis of the
     * same condition. `blocking` and `indeterminate` are reported separately because "this will
     * fail" and "we could not tell" call for different responses from the operator.
     */
    /**
     * ⛔⛔ THE DIAGNOSTIC FIELDS ARE VALIDATED, NOT ASSUMED — codex, minor at cbb5f5294, REPRODUCED.
     * `{ready:false, blocking:{}}` made `(readiness?.blocking || []).map(…)` throw a raw
     * "map is not a function" BEFORE reaching fail(). The clear stayed undispatched, so nothing was
     * lost — but the operator got an implementation error instead of "nothing was changed", and,
     * worse, a TypeError is not a RegenerateChainError: it carries no `weightsMayBeGone` and no
     * `discardedNote`, so the component's catch prints the bare message.
     * 🔑 THIS FILE ALREADY RECORDS THAT EXACT DEFECT, ONE SEAM EARLIER — a rejected promise used to
     *   escape as a raw Error and skip the ambiguity warning, which is why every request goes
     *   through `request()`. I closed the transport path and left the PARSING path open. A guard
     *   that switches itself off on malformed input is the thing this chain was rewritten to remove.
     * ⇒ `null` means PRESENT BUT NOT A LIST, which is unreadable rather than empty. It joins the
     *   no-readable-reason branch instead of silently contributing nothing.
     */
    const blockingMsgs = messagesOf(readiness?.blocking);
    const indeterminateMsgs = messagesOf(readiness?.indeterminate, (m) => `Could not verify: ${m}`);
    const reasons = blockingMsgs && indeterminateMsgs ? [...blockingMsgs, ...indeterminateMsgs] : null;
    fail(
      'This category is not ready to regenerate, so nothing was changed.'
      + (reasons && reasons.length
        ? `\n\n${reasons.join('\n\n')}`
        : '\n\nThe readiness check returned a reply this page could not read, so it is being treated '
          + 'as unverified rather than as permission.'),
    );
  }

  /**
   * ⛔⛔ `ready: true` IS NOT "EVERYTHING WAS VERIFIED", AND THE DIFFERENCE REACHES THE OPERATOR.
   * codex, BOTH LEGS BLOCKING @8c4fb6c2f, both reproduced: this read only `ready` and went straight
   * to the clear, so the server's `notChecked` warning — the one saying the sample check cannot
   * predict anything for a discovered sub-category — was thrown away at the last seam before the
   * irreversible step.
   * ⚠️ A malformed `notChecked` is UNREADABLE, not empty. Same `messagesOf` contract as the refusal
   *   path above: if the field is present but not a list we do not know what was left unverified,
   *   and proceeding on that is guessing at the one moment guessing is least affordable.
   */
  const unverified = messagesOf(readiness?.notChecked);
  if (unverified === null) {
    fail(
      'The readiness check reported something this page could not read about which checks it '
      + 'skipped, so it is being treated as unverified rather than as permission. Nothing was '
      + 'changed.',
    );
  } else if (unverified.length > 0) {
    // ⇒ The chain composes the dialog; the caller shows it. See the dep's contract note above:
    // this sentence is what an operator reads before authorising a destructive, paid write, and it
    // used to be authored in the page, where nothing executes it.
    const proceed = await confirmUnverified(describeUnverifiedConfirmation(unverified));
    if (!proceed) {
      fail(
        'Stopped before clearing the benefit weights — you chose not to proceed while part of the '
        + 'readiness check could not be verified. Nothing was changed.',
      );
    }
  }

  // ── 1. RECREATE — AND THIS IS NOW THE FIRST AND ONLY DESTRUCTIVE STEP ─────────────────────────
  /**
   * ⛔⛔ THE ORDER IS THE FIX (task-ukueazsg7n). There used to be a `clear-persona-weights` call
   * ahead of this one, and its ONLY purpose was to empty benefit_weights so that
   * preserveBenefitWeights would permit this write — a destructive write performed to disarm a
   * guard that exists to protect the data it destroys. Everything that could then fail — canonical
   * benefits, sample products, demographic evidence, the provider itself — failed AFTER the
   * curated vectors were already gone.
   * ⇒ NOW THE DESTRUCTIVE WRITE IS THIS ONE, WHICH MEANS IT ONLY HAPPENS IF THE RESEARCH SUCCEEDED.
   *   The failure class does not get predicted better; there is nothing left for it to happen after.
   *
   * ⛔ `replaceStrandedWeights` IS WHAT MAKES IT LEGAL, and it is opt-in per caller: the per-aspect
   * round arrow posts to this same endpoint and must keep the refusal. Jaume's ruling was about
   * this button.
   * ⭐ `expectedUpdatedAt` MOVED HERE WITH THE DESTRUCTION, and it is not merely relocated — it is
   * STRICTLY STRONGER. The client used to re-read the stamp between the clear and the recreate,
   * which has an unavoidable window between reading and writing. The server now compares it INSIDE
   * the row lock, in the same transaction as the write, so there is no window at all.
   */
  onPhase('recreating');
  recreateDispatched = true;
  const refreshRes = await request(`${base}/refresh/personas`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      replaceStrandedWeights: true,
      ...(expectedUpdatedAt ? { expectedUpdatedAt } : {}),
    }),
  },
    /**
     * ⛔⛔ THE UNCERTAINTY IS REAL; THE WEIGHTLESSNESS WAS NOT. This said the category "may
     * currently be WEIGHTLESS until the weighting step runs", and that cannot happen: the route
     * runs `validatePersonas` before it commits, which refuses any persona whose `benefit_weights`
     * is not a complete canonical vector of finite values (validatePersonas.js:984-1010). So a
     * committed recreate has, by construction, written full validated weights.
     * 🔑 THIS IS THE THIRD TIME ON THIS PR THE SAME FALSE SENTENCE HAS HAD TO COME OUT, and the
     *   previous two rounds are why it survived here: leg 2 flagged the WEIGHTING messages, I
     *   corrected exactly those, and this one is on the RECREATE — a sibling nobody had named.
     *   Fixing the reported instance leaves the defect and produces one review round per sibling.
     * ⚠️ What IS lost if it committed is the CURATED VALUES, replaced by machine ones — worth
     *   saying, and a different and much smaller claim than the category being unusable.
     */
    '⛔ The connection failed while recreating the personas, so it is NOT known whether the write committed. If it did, the category now has freshly recreated personas carrying validated benefit weights — it is usable — but your curated weights have been replaced and the dedicated re-weighting step has not run. Re-open the category and check before regenerating pages');
  /**
   * ⛔⛔ THIS COPY IS THE POINT OF THE WHOLE CHANGE AND IT USED TO SAY THE OPPOSITE.
   * It read: "The category now has its OLD personas with NO benefit weights — generation will use
   * the sector fallback until this is fixed." That was TRUE under the old order, because the clear
   * had already committed by the time this step could fail. It is FALSE now: a failed recreate
   * wrote nothing, so the stored personas AND their curated weights are exactly as they were.
   * ⇒ Telling an operator they have lost curated data when they have not is not a harmless stale
   *   sentence — it sends them to repair a category that is fine, and it teaches them to distrust
   *   the message on the day it IS true.
   */
  if (!refreshRes.ok) {
    /**
     * ⛔ THE BODY IS READ ONCE, AND BEFORE `fail`. A Response body can only be consumed once, so the
     * declaration and the message must come from the SAME parse — the clear's handling learned this
     * the hard way and this follows it rather than re-deriving it.
     */
    const refusal = await refreshRes.json().catch(() => null);
    if (refusal?.weightsWritten === false) refusedBeforeWrite = true;
    fail(
      (refusal?.weightsWritten === false
        ? 'The personas were NOT recreated and nothing was changed — your curated benefit weights '
          + 'are intact. '
        : '⛔ The recreate step failed and it is NOT known whether it wrote. If it did, the curated '
          + 'benefit weights have been replaced and the weighting step has not run — re-open the '
          + 'category and check the personas before regenerating pages. ')
      + `The recreate step failed: ${refusal?.error || `HTTP ${refreshRes.status}`}`,
    );
  }
  const refreshBody = await refreshRes.json().catch(() => null);
  if (refreshBody?.success !== true) {
    fail(
      '⛔ The recreate step returned a 200 that does not report success, so it is NOT known whether '
      + 'it wrote. If it did, the curated benefit weights have been replaced and the weighting step '
      + 'has not run — re-open the category and check the personas before regenerating pages.',
    );
  }
  /**
   * ⛔ THE EARLIEST POINT AT WHICH THE RECREATE'S OUTCOME IS KNOWN, AND IT IS EXACTLY HERE.
   * Two things are now established: the response was 2xx AND it reported `success: true`, which the
   * route sends only after its COMMIT. That is what this flag asks — "do we know what happened" —
   * and it is settled. It is NOT gated on the audit acknowledgement below: that gate answers a
   * different question (WHAT was replaced, and whether this backend is old enough to have skipped
   * the audit entirely), and it is a hard stop in its own right.
   * ⇒ PUTTING IT AFTER THAT GATE WOULD REINTRODUCE THE SELF-CONTRADICTING TOAST. I wrote it there
   *   first. On the no-acknowledgement path the chain tells the operator "your curated benefit
   *   weights are intact" — and the component would have appended "it MAY ALREADY HAVE COMMITTED,
   *   replacing the curated benefit weights" to that same message. One toast, both claims, which is
   *   the precise defect the `weightsWritten` contract was built to kill, arriving through a flag
   *   instead of through a status code.
   */
  recreateConfirmed = true;
  /**
   * ⛔⛔ THE FIELD IS AN ACKNOWLEDGEMENT THAT THE AUDITED OPERATION RAN — NOT A DECORATION, AND ITS
   * ABSENCE IS NOT AN EMPTY LIST (codex leg 1, BLOCKING at 6242286a4).
   *
   * I originally wrote `Array.isArray(...) ? ... : []` and justified it: "an older backend that does
   * not send the field must produce nothing to report, not a note built from undefined". That
   * reasoning was right about the NOTE and wrong about the GATE.
   * ⇒ THE MIXED-VERSION DEPLOYMENT: an older refresh handler IGNORES `replaceStrandedWeights`. With
   *   every stored key matching it then SUCCEEDS, preserving the curated vectors, and records no
   *   audit — and this client, reading the absent field as `[]`, would go on to call the weighting,
   *   which overwrites those vectors while recording only counts. The audit gap we spent three
   *   review rounds closing reopens silently, on exactly the window where an admin build is ahead
   *   of its backend.
   *
   * 🔑 ABSENT IS NOT EMPTY. I applied that rule to `notChecked` and to `weightsWritten` in this same
   *   file, argued it explicitly in both places, and then wrote its opposite here — because the
   *   compatibility default LOOKED harmless and I was thinking about the message rather than about
   *   what the field proves.
   *
   * ⚠️ REFUSING HERE IS SAFE, AND THAT IS WHY IT IS THE RIGHT ANSWER RATHER THAN A WARNING. The only
   *   way to reach this line on an old backend is the all-matched case — a stranded set would have
   *   been REFUSED by the old handler's guard — and in that case the curated vectors are intact.
   *   Stopping leaves the category with fresh personas and its original weights, which is a
   *   coherent state; continuing would destroy them unrecorded.
   */
  const replaced = refreshBody?.benefitWeightsReplaced;
  if (!Array.isArray(replaced)) {
    fail(
      'The personas were recreated, but this server did not confirm the audited replacement '
      + '(no benefitWeightsReplaced in its reply), so the regeneration has STOPPED before '
      + 're-weighting. Your curated benefit weights are intact and the category now carries the '
      + 'freshly researched personas. This normally means the backend is older than this page — '
      // ✅ CENSUS-CHECKED: this is the one post-commit path where "run it again" is SAFE, and the
      // reason is not obvious. Reaching here means the backend did not send the acknowledgement,
      // i.e. it predates this change and therefore has no recordBenefitReplacement at all — so
      // there is NO audit record to overwrite. (The curated vectors are also intact: an old handler
      // would have REFUSED anything that stranded, so a success here means every key matched.)
      + 'wait for the deployment to finish and run it again.',
    );
  }
  /**
   * ⛔⛔ THE VECTORS THE RECREATE *KEPT*, WHICH THE WEIGHTING STEP IS ABOUT TO OVERWRITE
   * (codex leg 1, minor at d7ba96287). The note used to be built from `replaced` alone, so the
   * ALL-MATCHED regeneration — every stored persona_key carried across, nothing stranded, so
   * `benefitWeightsReplaced` is legitimately `[]` — produced NO note at all, while the next step
   * replaced every one of those curated vectors and recorded only counts. A mixed run undercounted
   * for the same reason.
   * 🔑 I FIXED THIS EXACT CASE ON THE SERVER EARLIER IN THIS PR AND NOT HERE. `recordBenefitReplacement`
   *   was gated on `replaced` alone until both legs traced the all-matched path; I corrected the
   *   RECORD, so the durable trace exists — and left the only screen that would tell anyone about
   *   it reading the other field. FIXING THE LAYER YOU WERE SHOWN IS THE DEFECT THIS PR KEEPS
   *   REPRODUCING; this is its fifth appearance.
   * ⚠️ ABSENT IS NOT EMPTY, AND IT DOES NOT HALT HERE. The hard gate above stays on
   *   `benefitWeightsReplaced`, which is the acknowledgement that the audit RAN. This field is the
   *   NOTICE, and the durable record exists on the category either way, so an older backend that
   *   omits it earns an explicit caveat rather than a stopped regeneration — halting an operator
   *   over a message they can still recover from the row would be disproportionate.
   */
  const preservedRaw = refreshBody?.benefitWeightsPreserved;
  const preserved = Array.isArray(preservedRaw) ? preservedRaw : null;
  const atRisk = replaced.length + (preserved?.length ?? 0);
  if (atRisk > 0 || preserved === null) {
    /**
     * ⛔⛔ "AND IN THE SERVER LOG" WAS FALSE (codex leg 2, minor at 3ef84ed1f). Verified rather
     * than accepted: `recordBenefitReplacement.js` contains exactly one occurrence of the string
     * `console.` and it is at line 36, INSIDE A COMMENT describing the OLD clear endpoint — there
     * is no logging in the recorder or in its caller. The vectors exist in ONE place only.
     * 🔑 A POINTER TO A COPY THAT WAS NEVER WRITTEN IS WORSE THAN NO POINTER: it is read as a
     *   recovery option, so an operator goes looking for a safety net instead of acting while the
     *   one real record is still current.
     * ⚠️ AND THE ONE REAL RECORD IS NOT PERMANENT, WHICH IS THE PART WORTH SAYING OUT LOUD. The
     *   recorder's own header states the limit: `benefit_weight_replaced` holds the MOST RECENT
     *   replacement for the category, and regenerating again overwrites it. NAMING A LOCATION
     *   WITHOUT NAMING ITS LIFETIME INVITES EXACTLY THE DELAY THAT LOSES IT.
     */
    const counted = preserved === null
      ? `${replaced.length} curated weight vector(s) were replaced by this regeneration`
      : `${atRisk} curated weight vector(s) are at risk from this regeneration `
        + `(${replaced.length} replaced by the recreate, ${preserved.length} kept but about to be `
        + `overwritten by the re-weighting step)`;
    discardedNote = ` ${counted}. They are recorded on the category itself `
      + `(research_metadata.benefit_weight_replaced) and NOWHERE ELSE — there is no log copy. That `
      + `record holds only the MOST RECENT replacement, so regenerating this category again `
      + `overwrites it: copy the vectors now if you may need them.`;
    if (preserved === null) {
      discardedNote += ` ⚠️ This server did not report which vectors were KEPT and re-weighted, so `
        + `the count above may be LOW — read the record on the category for the full set.`;
    }
  }

  // ── 3. RE-WEIGHT ──────────────────────────────────────────────────────────────────────────────
  /**
   * ⚠️ NO STAMP CHECK HERE, DELIBERATELY, AND THE REASON MATTERS. I first wrote one: read the stamp
   * after the recreate, then assert it unchanged before the weighting. That is asserting a value
   * against ITSELF across a gap of microseconds — it can only ever pass, so it would have been a
   * check that looks like protection and provides none, costing two extra round-trips to say so.
   * ⇒ The only gap a client can meaningfully police is the one between the clear COMMITTING and the
   *   next write STARTING (checked above). Everything else happens DURING an LLM call, where no
   *   client-side check can see it — which is precisely the exposure the worker-job follow-up
   *   closes and why the rollout is gated on it.
   */
  onPhase('weighting');
  const weightsRes = await request(`${base}/research-persona-weights`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    // ⛔ EXPLICIT false. dryRun defaults TRUE server-side, so omitting this would report success
    // having persisted nothing — the exact silent-weightless outcome this chain exists to avoid.
    body: JSON.stringify({ dryRun: false }),
  }, 'The connection failed while re-researching the benefit weights, so it is not known whether they were written. The personas were recreated WITH validated weight vectors, so the category is usable either way.'
    + FINISH_WITHOUT_ERASING_THE_RECORD);
  if (!weightsRes.ok) {
    /**
     * ⛔⛔ THIS SAID "FRESH personas with NO benefit weights — generation will use the sector
     * fallback", AND IT IS FALSE UNDER THE NEW ORDER (codex, both legs, minor at f14dc3f4b).
     * The recreate runs `validatePersonas`, which REFUSES to commit a persona whose
     * benefit_weights is not a plain object of canonical ids with finite values in [0,1] and full
     * coverage — verified at validatePersonas.js:984-1010. So a committed recreate has already
     * written complete, validated vectors. A weighting failure means the RE-research did not run;
     * it does not mean the category is weightless.
     * 🔑 AND THIS IS MY OWN CORRECTION GOING WRONG IN THE OTHER DIRECTION. Two rounds ago leg 2
     *   told me the confirmation over-promised ("if it fails, nothing is changed"); I scoped it by
     *   adding a weightlessness warning that is itself untrue. A CORRECTION IS A NEW CLAIM AND
     *   INHERITS NONE OF THE ORIGINAL'S DISCIPLINE — and an over-warning is not the safe error: it
     *   sends an operator to run a destructive repair on a category that does not need one.
     */
    /**
     * ⛔⛔ A NON-2xx IS NOT PROOF THAT THE RE-WEIGHTING DID NOT COMMIT (codex leg 1, minor at
     * 3ef84ed1f), and this is the MIRROR of the server-side defect fixed in the same commit range:
     * there I removed a false UNCERTAINTY, here a false CERTAINTY.
     *
     * ⛔ THE WEIGHTING WRITE HAS NO TRANSACTION AT ALL. Verified rather than taken on trust:
     * PersonaBenefitWeightResearchService contains no BEGIN, no COMMIT and no ROLLBACK anywhere —
     * its only write is a bare `pool.query` UPDATE (:559-566), which AUTOCOMMITS the moment it
     * succeeds. The route then has ordinary ways to answer 500 afterwards: the rowCount guard that
     * throws immediately below that UPDATE (:571), any throw further up `research()`, a connection
     * lost while RECEIVING the result — postgres has committed, the client never learns it — or a
     * failure serialising the response. So "500" and "did not write" are different facts.
     * ⇒ Telling an operator their weights are definitely still the recreate's sends them to re-run
     *   a PAID research step that may already have succeeded.
     *
     * 🔑 AND THE SIBLING WAS ALREADY CORRECT, WHICH IS HOW THIS SURVIVED. The TRANSPORT message for
     *   this very step (a few lines above) says "it is not known whether they were written" — the
     *   honest form. Two paths out of one request, hedged on one and asserted on the other. That is
     *   the same shape as the WEIGHTLESS sentence: fix the path you are shown, leave the sibling.
     *
     * ⇒ Read the body ONCE, before `fail`, exactly as the recreate does — a Response body cannot be
     *   consumed twice, and this branch previously spent it inside the message via `failureOf`.
     * ⚠️ The weighting endpoint does not declare `weightsWritten` today, so this takes the UNKNOWN
     *   branch in practice. It is written as a declaration check rather than an unconditional
     *   "unknown" so that the day that endpoint gains the field, the client tightens automatically
     *   instead of needing someone to remember this line.
     */
    const weightsFailure = await weightsRes.json().catch(() => null);
    const weightingDeclaredNoWrite = weightsFailure?.weightsWritten === false;
    const cause = failureFrom(weightsFailure, `HTTP ${weightsRes.status}`);
    fail(
      weightingDeclaredNoWrite
        ? 'The personas were recreated with validated benefit weights, but the re-weighting step '
          + 'did NOT complete — the server confirmed it wrote nothing — so those weights are the '
          + 'ones the recreate produced rather than the purpose-researched ones. The category is '
          + `usable.${FINISH_WITHOUT_ERASING_THE_RECORD} The weighting step failed: ${cause}`
        : 'The personas were recreated with validated benefit weights, so the category is usable. '
          + 'The re-weighting step then failed, and it is NOT known whether it wrote: that step '
          + 'commits as soon as its update succeeds, so an error can be reported after the write '
          + 'landed. The weights are therefore either the recreate\'s or the purpose-researched '
          + `ones — re-open the category to see which.${FINISH_WITHOUT_ERASING_THE_RECORD}`
          + ` Repeating the weighting is also a paid research step. The weighting step failed: ${cause}`,
    );
  }
  const weightsBody = await weightsRes.json().catch(() => null);
  if (weightsBody?.success !== true) {
    fail(
      'The weighting step returned a 200 that does not report success, so it is not known whether '
      + 'the re-researched weights were written. The recreate committed validated weight vectors, so '
      + `the category is usable either way.${FINISH_WITHOUT_ERASING_THE_RECORD}`,
    );
  }
  /**
   * ⛔ THE CANONICAL LAYER IS REPORTED, NEVER INFERRED FROM A NULL (task-ush8qlgr72). The weighting
   * step returns canonicalPersonaIds beside a summary; a persona whose id is null is either
   * UNMAPPED (no synonym-map row for its name — the case every freshly recreated persona hits today,
   * because the map keys on the old display names) or FAILED (the lookup threw). Both are stated
   * here, in the operator's note, so "regenerated" can never be read as "canonicalised". A server
   * that omits the summary is said to be older, not read as "all mapped".
   */
  canonicalizationNote = describeCanonicalization(weightsBody?.result);

  // ── 4. VERIFY ─────────────────────────────────────────────────────────────────────────────────
  /**
   * ⚠️ THIS CHECKS THE PRECONDITION FOR category_scoped ROUTING, NOT fit_score_source ITSELF.
   * fit_score_source is stamped when a PAGE IS GENERATED, so it cannot exist before one runs. What
   * is checkable here is the thing that decides it: whether the stored personas came back carrying
   * weights. Claiming to have verified category_scoped would claim a reading that does not exist.
   */
  onPhase('verifying');
  const check = await request(base, undefined,
    'All regeneration steps reported success, but the connection failed during verification, so the result is UNCONFIRMED — re-open the category and check the personas carry benefit weights');
  if (!check.ok) {
    // ⛔ "COULD NOT READ THE ROW" IS NOT "THE ROW HAS NO PERSONAS". Collapsing them sends the
    // operator to the wrong recovery: one means re-run the chain, the other means look at the data.
    fail(
      `The regeneration steps all reported success, but the verification read FAILED `
      + `(${await failureOf(check, `HTTP ${check.status}`)}). The category was NOT confirmed — re-open `
      + `it and check the personas carry benefit weights before regenerating pages.`,
    );
  }
  const checkBody = await check.json().catch(() => null);

  const personas = checkBody?.data?.aspects?.personas?.data;
  if (!Array.isArray(personas) || personas.length === 0) {
    fail(`The chain reported success but the category has NO personas. Do not regenerate pages from it until this is understood.`);
  }
  const weightedCount = personas.filter(
    (p: any) => p?.benefit_weights && Object.keys(p.benefit_weights).length > 0,
  ).length;
  /**
   * ⛔⛔ EVERY PERSONA MUST BE WEIGHTED, NOT MERELY ONE (codex-reviewer blocking).
   * The routing gate only needs SOME persona to carry weights, so 1-of-5 still routes
   * category_scoped and my first check (weightedCount === 0) called that a success. But a persona
   * with no benefit_weights is INERT — nothing can ever route to it — so 1-of-5 means four buyers
   * silently cannot be matched while the category reports healthy.
   * ⇒ Partial weighting is NOT being established as a supported state: this button's entire promise
   *   is that it never leaves the category worse than it found it, and a set where most personas
   *   are unreachable is worse in a way that no screen shows.
   */
  if (weightedCount < personas.length) {
    fail(
      weightedCount === 0
        ? `⛔ ${personas.length} persona(s) were created but NONE carry benefit weights — generation `
          + `will use the sector fallback. Press "Repair benefit weights" before regenerating pages.`
        : `⛔ Only ${weightedCount} of ${personas.length} persona(s) carry benefit weights. The `
          + `${personas.length - weightedCount} without them are INERT — nothing can route to them — `
          + `even though the category will look healthy. Press "Repair benefit weights" to complete `
          + `it before regenerating pages.`,
    );
  }

  return { personaCount: personas.length, weightedCount, discardedNote, canonicalizationNote };
}
